import { useEffect, useId, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * A 12-hour AM/PM time control built from three native <select>s.
 *
 * Why not <input type="time">: browsers choose 12h vs 24h from the OS/browser
 * locale and expose no way to override it, so Indian users on an en-GB/de/fr
 * profile were being asked for a birth time in 24-hour form. Three selects are
 * unambiguous everywhere, and open the OS wheel picker on mobile (large touch
 * targets, no keyboard). The native time input also carries a UA intrinsic
 * width it refuses to shrink below, which is how it pushed past the edge of a
 * narrow card; these have explicit floors and wrap when the row runs out of
 * room, so they never overflow and never clip.
 *
 * The value in and out stays "HH:MM" 24-hour, so nothing downstream changes.
 */

export interface TimeInputProps {
  /** 24-hour "HH:MM", or '' for empty. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  /** Prefix for the per-select aria-labels, e.g. "Time of birth". */
  ariaLabel?: string;
  className?: string;
  /** Styling for each <select>, so the control matches its host form. */
  selectClassName?: string;
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

interface Parts {
  hour: string;
  minute: string;
  meridiem: '' | 'AM' | 'PM';
}

const EMPTY: Parts = { hour: '', minute: '', meridiem: '' };

/** "13:45" -> { hour: '01', minute: '45', meridiem: 'PM' }. */
export function partsFrom24h(value: string): Parts {
  const m = /^(\d{1,2}):(\d{2})/.exec(value ?? '');
  if (!m) return EMPTY;
  const h24 = Number(m[1]);
  if (!Number.isInteger(h24) || h24 < 0 || h24 > 23) return EMPTY;
  return {
    hour: String(h24 % 12 || 12).padStart(2, '0'),
    minute: m[2],
    meridiem: h24 < 12 ? 'AM' : 'PM',
  };
}

/** { hour: '01', minute: '45', meridiem: 'PM' } -> "13:45"; '' while incomplete. */
export function to24h({ hour, minute, meridiem }: Parts): string {
  if (!hour || !meridiem) return '';
  const h12 = Number(hour) % 12;
  const h24 = meridiem === 'PM' ? h12 + 12 : h12;
  return `${String(h24).padStart(2, '0')}:${minute || '00'}`;
}

/**
 * Floors below which each select would start clipping its own longest option
 * ("12", "59"/"MM", "AM/PM") once the 34px of padding and chevron gutter is
 * taken out, measured at 16px text — the largest any host uses.
 *
 * They are `min-width` on a wrapping row, not a share of the available space:
 * squeezing three controls into a half-width column silently truncates the
 * option text (a <select> clips without ellipsis and reports no overflow), so
 * where the row genuinely cannot fit, AM/PM drops to its own line instead.
 */
const MIN_HOUR = 64;
const MIN_MINUTE = 68;
const MIN_MERIDIEM = 92;

const DEFAULT_SELECT =
  'w-full min-w-0 rounded-lg border border-gray-700 bg-gray-900/70 px-2 py-2.5 text-sm text-white focus:border-gold-500 focus:outline-none';

export function TimeInput({
  value,
  onChange,
  disabled,
  required,
  ariaLabel = 'Time',
  className,
  selectClassName,
}: TimeInputProps) {
  const uid = useId();
  const [parts, setParts] = useState<Parts>(() => partsFrom24h(value));

  // Re-sync when the value is set from outside (saved-details prefill, reset).
  // Guarded on the round-trip so our own emits don't clobber a half-filled
  // control — while `meridiem` is unset we emit '', and blindly adopting that
  // would wipe the hour the user just picked.
  useEffect(() => {
    if (value === to24h(parts)) return;
    setParts(partsFrom24h(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (patch: Partial<Parts>) => {
    // Picking an hour implies :00 unless the user says otherwise — otherwise
    // "09 __ AM" would render as chosen but emit nothing.
    const next: Parts = { ...parts, ...patch };
    if (patch.hour && !next.minute) next.minute = '00';
    setParts(next);
    onChange(to24h(next));
  };

  const select = cn(
    '[color-scheme:dark] appearance-none bg-none',
    selectClassName ?? DEFAULT_SELECT,
    // After the host classes so a host's own pr-* can't reclaim the gutter the
    // chevron is drawn in.
    'pr-6',
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <Chevron min={MIN_HOUR}>
        <select
          id={`${uid}-hour`}
          aria-label={`${ariaLabel} — hour`}
          required={required}
          disabled={disabled}
          value={parts.hour}
          onChange={(e) => update({ hour: e.target.value })}
          className={select}
        >
          <option value="">HH</option>
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </Chevron>
      <Chevron min={MIN_MINUTE}>
        <select
          id={`${uid}-minute`}
          aria-label={`${ariaLabel} — minute`}
          disabled={disabled}
          value={parts.minute}
          onChange={(e) => update({ minute: e.target.value })}
          className={select}
        >
          <option value="">MM</option>
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </Chevron>
      <Chevron min={MIN_MERIDIEM}>
        <select
          id={`${uid}-meridiem`}
          aria-label={`${ariaLabel} — AM or PM`}
          required={required}
          disabled={disabled}
          value={parts.meridiem}
          onChange={(e) => update({ meridiem: e.target.value as Parts['meridiem'] })}
          className={select}
        >
          <option value="">AM/PM</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </Chevron>
    </div>
  );
}

/**
 * `appearance-none` removes the native arrow — needed so the three selects size
 * predictably rather than each reserving a UA-sized arrow gutter — and this
 * draws it back inside the `pr-6` the selects reserve for it.
 */
function Chevron({ children, min }: { children: React.ReactNode; min: number }) {
  return (
    <div className="relative flex-auto" style={{ minWidth: min, flexBasis: min }}>
      {children}
      <span
        aria-hidden
        className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-500"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </span>
    </div>
  );
}
